import type { MeetingDetail, MeetingPage, MinutesVersion } from '@fruuit/shared'
import { api } from '../../api/client'
import { type ApiQueryOptions, useApiQuery } from '../../api/hooks'

/** Which side of the portal is asking: `/app` reads the investor view, `/workspace` the team's. */
export type CalendarArea = 'app' | 'workspace'

export function viewOf(area: CalendarArea): 'investor' | 'staff' {
  return area === 'app' ? 'investor' : 'staff'
}

export const meetingKeys = {
  all: ['meetings'] as const,
  list: (area: CalendarArea, from: string, to: string) =>
    ['meetings', 'list', area, from, to] as const,
  detail: (area: CalendarArea, id: string) => ['meetings', 'detail', area, id] as const,
}

export function useMeeting(
  area: CalendarArea,
  id: string,
  options: ApiQueryOptions<MeetingDetail> = {},
) {
  return useApiQuery<MeetingDetail>(meetingKeys.detail(area, id), `/meetings/${id}`, {
    query: { view: viewOf(area) },
    ...options,
  })
}

/** All meetings in a range (follows the cursor; a month is well under a page). */
export async function fetchMeetingRange(
  area: CalendarArea,
  from: string,
  to: string,
  signal?: AbortSignal,
): Promise<MeetingPage> {
  let cursor: string | undefined
  let first: MeetingPage | undefined
  for (let i = 0; i < 5; i++) {
    const page: MeetingPage = await api.get<MeetingPage>('/meetings', {
      query: { from, to, view: viewOf(area), limit: 200, cursor },
      ...(signal ? { signal } : {}),
    })
    if (first) first.items.push(...page.items)
    else first = page
    if (!page.next_cursor) break
    cursor = page.next_cursor
  }
  return first as MeetingPage
}

type Ctx = { requestId: string }

export const meetingCommands = {
  create: (body: Record<string, unknown>, ctx: Ctx) =>
    api.post<MeetingDetail>('/meetings', body, ctx),
  update: (id: string, body: Record<string, unknown>, ctx: Ctx) =>
    api.patch<MeetingDetail>(`/meetings/${id}`, body, ctx),
  publish: (id: string, revision: number, ctx: Ctx) =>
    api.post<MeetingDetail>(`/meetings/${id}/publish`, { revision }, ctx),
  cancel: (id: string, revision: number, reason: string, ctx: Ctx) =>
    api.post<MeetingDetail>(`/meetings/${id}/cancel`, { revision, reason }, ctx),
  reschedule: (id: string, body: Record<string, unknown>, ctx: Ctx) =>
    api.post<MeetingDetail>(`/meetings/${id}/reschedule`, body, ctx),
}

export type MinutesStep = 'submit' | 'approve' | 'publish' | 'return'

export const minutesCommands = {
  create: (body: Record<string, unknown>, ctx: Ctx) =>
    api.post<MinutesVersion>('/minutes', body, ctx),
  update: (id: string, body: Record<string, unknown>, ctx: Ctx) =>
    api.patch<MinutesVersion>(`/minutes/${id}`, body, ctx),
  step: (id: string, step: MinutesStep, body: Record<string, unknown>, ctx: Ctx) =>
    api.post<MinutesVersion>(`/minutes/${id}/${step}`, body, ctx),
  correct: (id: string, body: Record<string, unknown>, ctx: Ctx) =>
    api.post<MinutesVersion>(`/minutes/${id}/correct`, body, ctx),
}
